import { ClearOutlined as ClearOutlinedIcon, Search as SearchIcon } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import is from '@sindresorhus/is';
import type { FunctionComponent, KeyboardEvent, KeyboardEventHandler } from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import Maybe from 'true-myth/maybe';

import { ApplicationContext } from '~/shared/context/context';
import type { SkillShort } from '~/shared/data-contract/skill';

import { TextButtonWithIcon } from '../../../design/components/button/TextButtonWithIcon';
import { CancelSearchButton } from '../../common/data-grids/common/CancelSearchButton';
import { useTalentDirectorySkillSearch } from '../../query-hooks/use-talent-directory-skill-search';
import { Dropdown } from './TableBarSkillSearchDropDown';

const skillTypeColorMap = {
    hard: 'hardskill',
    language: 'language',
    soft: 'softskill',
} as const;

const TableBarSkillSearchInput: FunctionComponent<{
    readonly onKeyEventHandler: (event: KeyboardEvent<HTMLInputElement>) => void;
    readonly onSearchButtonClick: () => void;
    readonly onSearchTermChanged: (value: Maybe<string>) => void;
    readonly onSkillDeselect: (skill: SkillShort) => void;
    readonly searchTerm: Maybe<string>;
    readonly skills: readonly SkillShort[];
}> = ({ onKeyEventHandler, onSearchButtonClick, onSearchTermChanged, onSkillDeselect, searchTerm, skills }) => {
    const theme = useTheme();
    const { actions, translate } = useContext(ApplicationContext);

    return (
        <Grid
            aria-label="Search Input"
            sx={{
                border: '1px solid',
                borderColor: 'cardborder.main',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                padding: '9px 12px',
            }}
        >
            <Grid width="100%">
                <Box
                    alignItems="center"
                    aria-label="Selected Skills"
                    display="flex"
                    flexWrap="wrap"
                    gap={0.25}
                    justifyContent="start"
                >
                    {skills.map((skill) => (
                        <TextButtonWithIcon
                            key={skill.id}
                            backgroundColor={theme.palette[skillTypeColorMap[skill.skillType]].main}
                            Icon={ClearOutlinedIcon}
                            label={skill.title}
                            textColor="#FFF"
                            onIconClick={() => onSkillDeselect(skill)}
                            onLabelClick={() => actions.openSkillDetails(skill.id)}
                        />
                    ))}
                </Box>
                <TextField
                    aria-label="Search Input Field"
                    autoComplete="off"
                    fullWidth={true}
                    id="search-input"
                    InputProps={{ disableUnderline: true }}
                    placeholder={translate('pages.talent-directory.filters.skill-search.placeholder')}
                    size="small"
                    sx={{ padding: '5px 0' }}
                    value={searchTerm.unwrapOr('')}
                    variant="standard"
                    onChange={(event) => {
                        const { value } = event.target;
                        onSearchTermChanged(is.nonEmptyString(value) ? Maybe.just(value) : Maybe.nothing());
                    }}
                    onKeyDown={onKeyEventHandler}
                />
            </Grid>
            <IconButton
                disableRipple={true}
                id="search-button"
                sx={{ height: '24px', width: '24px' }}
                onClick={(event) => {
                    event.stopPropagation();
                    onSearchButtonClick();
                }}
            >
                <SearchIcon />
            </IconButton>
        </Grid>
    );
};

export const TableBarSkillSearch: FunctionComponent<{
    readonly onSearchBarCancelButtonClick: () => void;
    readonly onSkillDeselected: (skill: SkillShort) => void;
    readonly onSkillSearchButtonClick: (selectedSkills: readonly SkillShort[]) => void;
    readonly onSkillSelected: (skill: SkillShort) => void;
    readonly selectedSkills: readonly SkillShort[];
}> = ({
    onSearchBarCancelButtonClick,
    onSkillDeselected,
    onSkillSearchButtonClick,
    onSkillSelected,
    selectedSkills,
}) => {
    const dropdownPositionRef = useRef<HTMLDivElement>(null);
    const [searchTerm, setSearchTerm] = useState<Maybe<string>>(Maybe.nothing());
    const [dropDownOpen, setDropDownOpen] = useState(false);
    const talentDirectorySearchQuery = useTalentDirectorySkillSearch({
        enabled: searchTerm.isJust,
        searchTerm,
    });
    const [currentCursor, setCurrentCursor] = useState<number>(-1);
    const dropDownSkillItems =
        talentDirectorySearchQuery.data?.filter(
            ({ id }) => !selectedSkills.some(({ id: selectedId }) => selectedId === id),
        ) ?? [];

    const onSelectSkill = (skill: SkillShort): void => {
        setSearchTerm(Maybe.nothing());
        setDropDownOpen(false);
        onSkillSelected(skill);
    };

    const onSearchButtonClick = (): void => {
        setSearchTerm(Maybe.nothing());
        onSkillSearchButtonClick(selectedSkills);
        setDropDownOpen(false);
    };

    const onKeyEventHandler: KeyboardEventHandler<HTMLInputElement> = (event) => {
        switch (event.key) {
            case 'ArrowDown': {
                if (!is.plainObject(dropDownSkillItems[currentCursor + 1])) {
                    setCurrentCursor((prevState) => prevState + 1);
                }
                break;
            }
            case 'ArrowUp': {
                if (is.plainObject(dropDownSkillItems[currentCursor - 1])) {
                    setCurrentCursor((prevState) => prevState - 1);
                } else {
                    setCurrentCursor(-1);
                }
                break;
            }
            case 'Backspace': {
                const deselectedSkill = selectedSkills.at(-1);

                if (
                    is.emptyString(searchTerm) &&
                    is.nonEmptyArray(selectedSkills) &&
                    !is.nullOrUndefined(deselectedSkill)
                ) {
                    onSkillDeselected(deselectedSkill);
                }
                break;
            }
            case 'Enter': {
                const skill = dropDownSkillItems[currentCursor];

                if (is.plainObject(skill)) {
                    onSkillSelected(skill);
                    setCurrentCursor(-1);
                }
                break;
            }
            default:
                break;
        }
    };

    const currentIndex = dropDownSkillItems[currentCursor]?.id ?? '';

    useEffect(() => {
        if (searchTerm.isJust) {
            setDropDownOpen(true);
        }
    }, [searchTerm]);

    return (
        <Box
            sx={{
                alignItems: 'center',
                display: 'flex',
                gap: 3,
                paddingY: 1,
                width: '100%',
            }}
        >
            <Stack
                ref={dropdownPositionRef}
                spacing={2}
                sx={{
                    display: 'flex',
                    width: '100%',
                }}
            >
                <TableBarSkillSearchInput
                    searchTerm={searchTerm}
                    skills={selectedSkills}
                    onKeyEventHandler={onKeyEventHandler}
                    onSearchButtonClick={onSearchButtonClick}
                    onSearchTermChanged={setSearchTerm}
                    onSkillDeselect={onSkillDeselected}
                />
                {talentDirectorySearchQuery.isSuccess && dropDownOpen && searchTerm.isJust && (
                    <Dropdown
                        activeId={currentIndex}
                        positionRef={dropdownPositionRef}
                        searchTerm={searchTerm.value}
                        skills={dropDownSkillItems}
                        onSkillSelected={onSelectSkill}
                    />
                )}
            </Stack>
            <CancelSearchButton onClick={onSearchBarCancelButtonClick} />
        </Box>
    );
};
